import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GateProvider } from './context/GateContext';
import Header from './components/common/Header';
import Home from './pages/Home';
import SectionPage from './pages/SectionPage';
import EntryDetail from './pages/EntryDetail';
import AdminLogin from './pages/Admin/Login';
import AdminDashboard from './pages/Admin/Dashboard';
import EntryForm from './pages/Admin/EntryForm';
import ManageContent from './pages/Admin/ManageContent';
import PeopleManager from './pages/Admin/PeopleManager';
import TraditionManager from './pages/Admin/TraditionManager';
import RecipeManager from './pages/Admin/RecipeManager';
import SecuritySettings from './pages/Admin/SecuritySettings';

// Helper component to fix "Blank Screen" on navigation by resetting scroll
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdfcf9]">
        <div className="w-8 h-8 border-4 border-[#8c7851] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/admin/login" replace />;
};

function App() {
  return (
    <AuthProvider>
      <GateProvider>
        <Router>
          <ScrollToTop />
          <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/section/:sectionId" element={<SectionPage />} />
                <Route path="/entry/:id" element={<EntryDetail />} />
                
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
                <Route path="/admin/entries" element={<PrivateRoute><ManageContent /></PrivateRoute>} />
                <Route path="/admin/entries/new" element={<PrivateRoute><EntryForm /></PrivateRoute>} />
                <Route path="/admin/entries/edit/:id" element={<PrivateRoute><EntryForm /></PrivateRoute>} />
                <Route path="/admin/people" element={<PrivateRoute><PeopleManager /></PrivateRoute>} />
                <Route path="/admin/traditions" element={<PrivateRoute><TraditionManager /></PrivateRoute>} />
                <Route path="/admin/recipes" element={<PrivateRoute><RecipeManager /></PrivateRoute>} />
                <Route path="/admin/settings" element={<PrivateRoute><SecuritySettings /></PrivateRoute>} />
                
                {/* Catch-all redirect to Home */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
            <footer className="py-12 border-t border-[var(--border)] text-center text-xs text-[var(--text-secondary)]">
              &copy; {new Date().getFullYear()} Dimovski Family Archive. Built with love.
            </footer>
          </div>
          <Toaster position="bottom-right" />
        </Router>
      </GateProvider>
    </AuthProvider>
  );
}

export default App;